import { useCallback, useEffect, useRef, useState } from 'react';
import type { ViewMode } from '../../domain/types';
import { WorkspaceToolType } from '../modals/ToolDrawerModal';
import { OPEN_SETTINGS_EVENT, shouldOpenSettingsFromSearch } from '../settings/settingsDeepLink';

type NavigateDeps = {
  setView: (view: ViewMode) => void;
  selectItem: (id: string | null) => void;
};

export function useMainLayoutChrome(isCompact: boolean, deps: NavigateDeps) {
  const initialParams =
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search);
  const [isSettingsOpen, setIsSettingsOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return shouldOpenSettingsFromSearch(window.location.search);
  });
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const initialTool = initialParams?.get('tool');
  const [activeTool, setActiveToolState] = useState<WorkspaceToolType>(
    initialTool === 'daily' || initialTool === 'notes' || initialTool === 'wiki'
      ? initialTool
      : null,
  );
  const sidebarRef = useRef<HTMLElement | null>(null);
  const drawerTriggerRef = useRef<HTMLElement | null>(null);

  const openSettings = useCallback(() => {
    setIsSettingsOpen(true);
    setIsDrawerOpen(false);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('settings', 'providers');
      url.searchParams.delete('provider');
      url.searchParams.delete('oauth');
      url.searchParams.delete('jira');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);
  const closeSettings = useCallback(() => {
    setIsSettingsOpen(false);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('settings');
      url.searchParams.delete('provider');
      url.searchParams.delete('oauth');
      url.searchParams.delete('jira');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);
  const setActiveTool = useCallback((tool: WorkspaceToolType) => {
    setActiveToolState(tool);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (tool) url.searchParams.set('tool', tool);
      else url.searchParams.delete('tool');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);
  const closeActiveTool = useCallback(() => setActiveTool(null), [setActiveTool]);

  useEffect(() => {
    if (shouldOpenSettingsFromSearch(window.location.search)) {
      setIsSettingsOpen(true);
      setIsDrawerOpen(false);
    }
  }, []);

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ section?: string }>).detail;
      if (detail?.section && typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.set('settings', detail.section);
        url.searchParams.delete('provider');
        url.searchParams.delete('oauth');
        url.searchParams.delete('jira');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        setIsSettingsOpen(true);
        setIsDrawerOpen(false);
        return;
      }
      openSettings();
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, onOpen);
  }, [openSettings]);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    const trigger = drawerTriggerRef.current;
    if (trigger) {
      window.setTimeout(() => trigger.focus(), 0);
    }
  }, []);

  const openDrawer = useCallback((trigger?: HTMLElement | null) => {
    if (trigger) drawerTriggerRef.current = trigger;
    setIsDrawerOpen(true);
  }, []);

  const { setView, selectItem } = deps;
  const navigateToView = useCallback(
    (next: ViewMode) => {
      setView(next);
      selectItem(null);
      setIsDrawerOpen(false);
    },
    [setView, selectItem],
  );

  const openTool = useCallback(
    (tool: Exclude<WorkspaceToolType, null>) => {
      setActiveTool(tool);
      setIsDrawerOpen(false);
    },
    [setActiveTool],
  );

  useEffect(() => {
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      const tool = params.get('tool');
      setActiveToolState(tool === 'daily' || tool === 'notes' || tool === 'wiki' ? tool : null);
      setIsSettingsOpen(shouldOpenSettingsFromSearch(window.location.search));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (!isCompact) setIsDrawerOpen(false);
  }, [isCompact]);

  useEffect(() => {
    if (!isCompact || !isDrawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isCompact, isDrawerOpen, closeDrawer]);

  useEffect(() => {
    if (!isCompact || !isDrawerOpen || !sidebarRef.current) return;
    const focusable = sidebarRef.current.querySelector<HTMLElement>(
      'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    );
    window.setTimeout(() => (focusable ?? sidebarRef.current)?.focus(), 0);
  }, [isCompact, isDrawerOpen]);

  return {
    isSettingsOpen,
    openSettings,
    closeSettings,
    isSidebarExpanded,
    setIsSidebarExpanded,
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    activeTool,
    setActiveTool,
    closeActiveTool,
    openTool,
    navigateToView,
    sidebarRef,
  };
}
